import { createSlice, type PayloadAction } from '@databases/utils/shims/redux-toolkit';
import { pullAll, pullAllWith, union, unionWith } from 'lodash-es';

export type SelectedValue = { fieldId: string; entryId: string };

export type SelectedItem =
  | {
      type: 'field';
      fieldId: string;
    }
  | {
      type: 'entry';
      entryId: string;
    }
  | {
      type: 'value';
      fieldId: string;
      entryId: string;
    };

export interface CellSelectionsState {
  /**
   * The selected entire field IDs, in undefined order.
   */
  selectedEntireFields: string[];

  /**
   * The selected entire entry IDs, in undefined order.
   */
  selectedEntireEntries: string[];

  /**
   * The selected values, in undefined order. Does not include values implicitly selected through entire fields/entries.
   */
  selectedValues: SelectedValue[];

  /**
   * The last selected/unselected field/entry/value that should be used as the base for range selections.
   */
  focusedItem: SelectedItem | undefined;

  /**
   * If true, indicates that the user has not explicitly made the current selection, but some functionality of the app has
   * implicitly made the selection to indicate context to the user, resulting in a less emphasized visualization of the
   * selection. An example would be that opening the field settings sidebar will "softly" select its field to indicate
   * which field the settings are for.
   */
  softSelectEnabled: boolean;

  hoverDeleteButton: 'fields' | 'entries' | null;

  /**
   * The first selected value, potentially in a multiselect sequence.
   */
  firstSelectedValue: SelectedValue | null;
}

export const initialCellSelectionsState: CellSelectionsState = {
  selectedEntireFields: [],
  selectedEntireEntries: [],
  selectedValues: [],
  focusedItem: undefined,
  softSelectEnabled: false,
  hoverDeleteButton: null,
  firstSelectedValue: null
};

const cellSelectionsSlice = createSlice({
  name: 'cellSelections',
  initialState: initialCellSelectionsState,
  reducers: {
    selectFields: (
      state,
      action: PayloadAction<{
        contentId: string;
        fieldIds: string[];
        clear: boolean;
        focusFieldId: string | undefined;
        clearSelectedValues?: boolean;
      }>
    ) => {
      const {
        fieldIds,
        clear,
        focusFieldId,
        clearSelectedValues = true
      } = action.payload;

      state.selectedEntireEntries = [];
      if (clearSelectedValues) {
        state.selectedValues = [];
      }
      state.selectedEntireFields = clear
        ? fieldIds
        : union(state.selectedEntireFields, fieldIds);
      state.focusedItem =
        focusFieldId != null ? { type: 'field', fieldId: focusFieldId } : focusFieldId;
    },

    deselectFields: (
      state,
      action: PayloadAction<{ fieldIds: string[]; focusFieldId: string | undefined }>
    ) => {
      const { fieldIds, focusFieldId } = action.payload;

      pullAll(state.selectedEntireFields, fieldIds);
      state.focusedItem =
        focusFieldId != null ? { type: 'field', fieldId: focusFieldId } : undefined;
    },

    selectEntries: (
      state,
      action: PayloadAction<{
        contentId: string; // used by live presence middleware
        entryIds: string[];
        clear: boolean;
        focusEntryId: string | undefined;
        clearSelectedValues?: boolean;
      }>
    ) => {
      const {
        entryIds,
        clear,
        focusEntryId,
        clearSelectedValues = true
      } = action.payload;
      state.selectedEntireFields = [];
      if (clearSelectedValues) {
        state.selectedValues = [];
      }
      state.selectedEntireEntries = clear
        ? entryIds
        : union(state.selectedEntireEntries, entryIds);
      state.focusedItem =
        focusEntryId != null ? { type: 'entry', entryId: focusEntryId } : undefined;
    },

    deselectEntries: (
      state,
      action: PayloadAction<{
        contentId: string;
        entryIds: string[];
        focusEntryId: string | undefined;
      }>
    ) => {
      const { entryIds, focusEntryId } = action.payload;

      pullAll(state.selectedEntireEntries, entryIds);
      state.focusedItem =
        focusEntryId != null ? { type: 'entry', entryId: focusEntryId } : undefined;
    },

    selectValues: (
      state,
      action: PayloadAction<{
        contentId: string; // used by live presence middleware
        values: SelectedValue[];
        clear: boolean;
        focusValue: SelectedValue | undefined;
      }>
    ) => {
      const { clear, values, focusValue } = action.payload;

      state.selectedEntireFields = [];
      state.selectedEntireEntries = [];
      state.selectedValues = clear
        ? values
        : unionWith(
            state.selectedValues,
            values,
            (a, b) => a.fieldId === b.fieldId && a.entryId === b.entryId
          );
      state.focusedItem = focusValue && { ...focusValue, type: 'value' };
    },

    deselectValues: (
      state,
      action: PayloadAction<{
        values: SelectedValue[];
        focusValue: SelectedValue | undefined;
      }>
    ) => {
      const { values, focusValue } = action.payload;

      pullAllWith(
        state.selectedValues,
        values,
        (a, b) => a.fieldId === b.fieldId && a.entryId === b.entryId
      );
      state.focusedItem = focusValue && { ...focusValue, type: 'value' };
    },

    deselectAll: (state) => {
      state.selectedEntireFields = [];
      state.selectedEntireEntries = [];
      state.selectedValues = [];
      state.focusedItem = undefined;
      state.firstSelectedValue = null;
    },

    setSoftSelection: (state, action: PayloadAction<{ value: boolean }>) => {
      const { value } = action.payload;

      state.softSelectEnabled = value;
    },

    setHoverDeleteButton: (
      state,
      action: PayloadAction<{ value: CellSelectionsState['hoverDeleteButton'] }>
    ) => {
      const { value } = action.payload;

      state.hoverDeleteButton = value;
    },

    setFirstSelectedValue: (
      state,
      action: PayloadAction<{ value: SelectedValue | null }>
    ) => {
      const { value } = action.payload;

      state.firstSelectedValue = value;
    }
  }
});

export default cellSelectionsSlice;
